// src/components/resources/tools/ToolsHubSearchTrigger.tsx

'use client'

import { openSiteSearch } from '~/components/search/SiteSearch'
import { useSiteSearchShortcutHint } from '~/components/search/useSiteSearchShortcutHint'
import RiSearchLine from '~/icons/remix/generated/RiSearchLine'

/**
 * The tools hub's search slot (hub-redesign slice 1): a search-field-shaped
 * button that opens the global ⌘K palette (`SiteSearch`), attributed to this
 * surface via `openSiteSearch('resources-tools-hub')`.
 *
 * Deliberately NOT a text input and NOT a second palette: the palette host +
 * corpus/index already live behind the navbar's `SiteSearch` mount (lazy on
 * first open), so this island ships only a button and one platform-detection
 * effect — no new search code, no corpus fetch, no layout reflow when
 * richer hub search treatments land (the slot is already occupied).
 */
export function ToolsHubSearchTrigger(): JSX.Element {
  // Shared with SiteSearchTrigger so the hint heuristic can't drift (`r3547477910`).
  const shortcutHint = useSiteSearchShortcutHint()

  return (
    <button
      type="button"
      // Source string deliberately contains "resources" so
      // `surfaceFromLocation` classifies the open under the `resources`
      // surface (a bare 'tools-hub' fell through to `unknown`); the full
      // string still lands in `element_text` for hub-level attribution.
      onClick={() => openSiteSearch('resources-tools-hub')}
      // No explicit aria-label: the search icon and the ⌘K kbd are both
      // aria-hidden, so the accessible name is computed from the visible span
      // alone — "Search tools & all resources…", ellipsis included. That makes
      // the accessible name EQUAL the visible label (WCAG 2.5.3 label-in-name,
      // strictly satisfied, `r3558350355`) with zero aria-label⇆text sync
      // burden. Guarded by tools-hub-directory-links.test.tsx.
      aria-haspopup="dialog"
      aria-keyshortcuts="Meta+K Control+K"
      className="group flex w-full max-w-xl items-center gap-3 rounded-xl border border-gray-200 bg-white/70 px-4 py-3 text-left text-gray-500 shadow-sm transition-colors hover:border-indigo-300 hover:text-gray-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 dark:border-white/10 dark:bg-gray-900/60 dark:text-gray-400 dark:hover:border-indigo-500/60 dark:hover:text-gray-200"
    >
      <RiSearchLine aria-hidden="true" className="size-5 shrink-0 text-gray-400 dark:text-gray-500" />
      <span className="flex-1 truncate text-sm sm:text-base">
        Search tools &amp; all resources…
      </span>
      <kbd
        aria-hidden="true"
        className="hidden shrink-0 rounded bg-gray-100 px-1.5 py-0.5 text-xs font-semibold text-gray-500 sm:inline-flex dark:bg-gray-800 dark:text-gray-400"
      >
        {shortcutHint}
      </kbd>
    </button>
  )
}
